import React, { useEffect, useState, useRef } from "react";
import { Table, Thead, Tbody, Tr, Th, Td, Spinner, useTheme, Box, Spacer, Heading, VStack, useColorModeValue } from "@chakra-ui/react";

import axios from "axios";

const Table1 = ({ supplier, retailer }) => {
  const [thead, setThead] = useState({ th0: [], th1: [] });
  const [tbody, setTbody] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const syncingRef = useRef(false);

  useEffect(() => {
    const tableContainer = document.getElementById("table-container");
    const stickyScrollbar = document.getElementById("sticky-scrollbar");
    const scrollbarContent = document.getElementById("scrollbar-content");

    if (!tableContainer || !stickyScrollbar || !scrollbarContent) return;

    // Set the width of the scrollbar content to match table width
    const updateScrollbarWidth = () => {
      const table = tableContainer.querySelector("table");
      if (table) {
        const tableWidth = table.scrollWidth;
        const containerWidth = tableContainer.clientWidth;
        scrollbarContent.style.width = tableWidth + "px";

        // Show/hide scrollbar based on whether scrolling is needed
        if (tableWidth > containerWidth) {
          stickyScrollbar.style.display = "block";
        } else {
          stickyScrollbar.style.display = "none";
        }
      }
    };

    // Improved sync functions with debouncing
    const syncTableScroll = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      requestAnimationFrame(() => {
        stickyScrollbar.scrollLeft = tableContainer.scrollLeft;
        syncingRef.current = false;
      });
    };

    const syncStickyScroll = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      requestAnimationFrame(() => {
        tableContainer.scrollLeft = stickyScrollbar.scrollLeft;
        syncingRef.current = false;
      });
    };

    // Initial setup with proper timing
    const initializeScrollbar = () => {
      updateScrollbarWidth();
      // Sync initial scroll positions
      stickyScrollbar.scrollLeft = tableContainer.scrollLeft;
    };

    // Use setTimeout to ensure DOM is fully rendered
    setTimeout(initializeScrollbar, 150);

    // Add event listeners with passive option for better performance
    tableContainer.addEventListener("scroll", syncTableScroll, { passive: true });
    stickyScrollbar.addEventListener("scroll", syncStickyScroll, { passive: true });

    // Update scrollbar width when window resizes
    const handleResize = () => {
      setTimeout(updateScrollbarWidth, 100);
    };
    window.addEventListener("resize", handleResize);

    // Observer to update scrollbar when table content changes
    const observer = new MutationObserver(() => {
      setTimeout(updateScrollbarWidth, 50);
    });
    observer.observe(tableContainer, { childList: true, subtree: true, attributes: true });

    return () => {
      tableContainer.removeEventListener("scroll", syncTableScroll);
      stickyScrollbar.removeEventListener("scroll", syncStickyScroll);
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
    };
  }, [tbody, thead]);

  useEffect(() => {
    const el = document.getElementById("table-container");
    if (!el) return;

    let down = false;
    let startX = 0;
    let scrollStart = 0;

    const downHandler = (e) => {
      if (e.button !== 0) return;
      down = true;
      startX = e.clientX;
      scrollStart = el.scrollLeft;
      el.style.cursor = "grabbing";
      el.style.userSelect = "none";
    };

    const moveHandler = (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      el.scrollLeft = scrollStart - dx;
    };

    const upHandler = () => {
      if (!down) return;
      down = false;
      el.style.cursor = "grab";
      el.style.removeProperty("user-select");
    };

    el.addEventListener("mousedown", downHandler);
    window.addEventListener("mousemove", moveHandler);
    window.addEventListener("mouseup", upHandler);

    return () => {
      el.removeEventListener("mousedown", downHandler);
      window.removeEventListener("mousemove", moveHandler);
      window.removeEventListener("mouseup", upHandler);
    };
  }, []);

  useEffect(() => {
    const buildTable = async () => {
      try {
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/rep_ranging/html-rangingReport`, {
          supplier,
          retailer,
          userid: localStorage.getItem("userid"),
        });
        const { status, data, message } = res.data;
        if (status === "success") {
          setThead(data.head);
          setTbody(data.body);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching table data:", error);
        setLoading(false);
      }
    };

    buildTable();
  }, [supplier, retailer]);

  if (loading) return <Spinner />;

  return (
    <Box position="relative">
      {/* Table-specific Sticky Horizontal Scrollbar at Top */}
      <Box
        id="sticky-scrollbar"
        position="sticky"
        top={0}
        left={0}
        right={0}
        height="18px"
        bg="white"
        borderBottom="2px solid"
        borderColor={theme.colors.primary1}
        zIndex={25}
        display="none"
        overflowX="auto"
        overflowY="hidden"
        css={{
          scrollBehavior: "auto",
          "&::-webkit-scrollbar": {
            height: "14px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f5f9",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.colors.primary1,
            borderRadius: "4px",
            border: "1px solid #e2e8f0",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: theme.colors.primary2,
          },
          "&::-webkit-scrollbar-thumb:active": {
            background: theme.colors.primary2,
          },
        }}
      >
        <Box id="scrollbar-content" height="1px" minWidth="100%" />
      </Box>

      <Box id="table-container" overflowX="auto" overflowY="hidden" sx={{ cursor: "grab", scrollBehavior: "auto" }}>
        <Table
          size="sm"
          variant="unstyled"
          width="max-content"
          sx={{
            minWidth: "max-content",
            tableLayout: "fixed",
            "th:nth-of-type(n+4), td:nth-of-type(n+4)": {
              maxWidth: "50px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
        >
          <Thead position="sticky" top="18px" zIndex={20} bg={theme.colors.primary2} color="white" shadow="md">
            <Tr>
              {thead.th0.map((th, index) => (
                <Th key={`th0-${index}`} width={index === 0 ? "80px" : index === 1 ? "180px" : index === 2 ? "120px" : "70px"} borderRight="1px solid" borderColor="whiteAlpha.400" position={index < 2 ? "sticky" : "static"} left={index === 0 ? 0 : index === 1 ? "80px" : "auto"} zIndex={22} bg={theme.colors.primary2} color="white" px={2} py={2} fontSize="10px" fontWeight="bold" textTransform="uppercase" letterSpacing="wide" shadow={index < 2 ? "md" : "none"} lineHeight="1.2">
                  <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    {th}
                  </Box>
                </Th>
              ))}
            </Tr>
            <Tr>
              {thead.th1.map((th, index) => (
                <Th key={`th1-${index}`} width={index === 0 ? "80px" : index === 1 ? "180px" : index === 2 ? "120px" : "70px"} borderRight="1px solid" borderColor="whiteAlpha.400" position={index < 2 ? "sticky" : "static"} left={index === 0 ? 0 : index === 1 ? "80px" : "auto"} zIndex={22} bg={theme.colors.secondary1} color="white" px={2} py={1.5} fontSize="9px" fontWeight="medium" shadow={index < 2 ? "md" : "none"} lineHeight="1.1">
                  <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    {th}
                  </Box>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {tbody.map((row, rowIndex) => (
              <Tr key={`row-${rowIndex}`} bg={rowIndex % 2 === 0 ? "gray.50" : "white"} _hover={{ bg: "blue.50" }} transition="all 0.15s" borderBottom="1px solid" borderColor="gray.200">
                {row.map((cell, cellIndex) => (
                  <Td
                    key={`cell-${rowIndex}-${cellIndex}`}
                    sx={{
                      backgroundColor: cellIndex < 2 ? (rowIndex % 2 === 0 ? "gray.50" : "white") : `${cell.color} !important`,
                      whiteSpace: "nowrap",
                      minWidth: cellIndex === 0 ? "80px" : cellIndex === 1 ? "180px" : cellIndex === 2 ? "120px" : "70px",
                    }}
                    width={cellIndex === 0 ? "80px" : cellIndex === 1 ? "180px" : cellIndex === 2 ? "120px" : "70px"}
                    borderRight="1px solid"
                    borderColor="gray.200"
                    position={cellIndex < 2 ? "sticky" : "static"}
                    left={cellIndex === 0 ? 0 : cellIndex === 1 ? "80px" : "auto"}
                    zIndex={cellIndex < 2 ? 15 : 1}
                    px={2}
                    py={1}
                    fontSize="11px"
                    fontWeight={cellIndex < 2 ? "medium" : "normal"}
                    shadow={cellIndex < 2 ? "sm" : "none"}
                    lineHeight="1.3"
                  >
                    <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={cell.value} maxW="100%">
                      {cell.value}
                    </Box>
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default Table1;
